import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import {
  Box,
  TextField,
  Grid,
  Button,
  Typography,
  MenuItem,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Checkbox,
  FormControlLabel,
  Stack,
  List,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Divider,
  LinearProgress,
  Slide,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import SkillsIcon from "@mui/icons-material/Psychology";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import { Trash2 } from "lucide-react";

import { AuthContext } from "../context/AuthContext";
import { ErrorContext } from "../context/ErrorContext";
import countries from "./countries.json";
import { useSearchParams } from "react-router-dom";
import { createAxiosInstance } from "../api/axios";
import { rankResumeWithErrorHandling } from "../api/resumeRanking";
import GradeScoreDisplay from "./GradeScoreDisplay";

const validate = (data) => {
  const errors = {};
  if (!data.first_name) errors.first_name = "First name is required";
  if (!data.last_name) errors.last_name = "Last name is required";
  if (!data.date_of_birth) errors.date_of_birth = "Date of birth is required";
  if (!data.address_line_1)
    errors.address_line_1 = "Address Line 1 is required";
  if (!data.city) errors.city = "City is required";
  if (!data.state) errors.state = "State/Province is required";
  if (!data.country) errors.country = "Country is required";
  if (!data.mobile_number) errors.mobile_number = "Mobile number is required";
  // Add more validation as needed
  return errors;
};

// Validate experience entries
const validateExperience = (experienceList) => {
  const errors = {};
  experienceList.forEach((exp, index) => {
    if (!exp.company?.trim()) {
      errors[`experience_${index}_company`] = `Experience #${index + 1}: Company field cannot be empty`;
    }
    if (!exp.title?.trim()) {
      errors[`experience_${index}_title`] = `Experience #${index + 1}: Job title is required`;
    }
    if (!exp.description?.trim()) {
      errors[`experience_${index}_description`] = `Experience #${index + 1}: Description is required`;
    }
    
    // Validate date format (YYYY-MM)
    if (exp.start_date && !/^\d{4}-\d{2}$/.test(exp.start_date)) {
      errors[`experience_${index}_start_date`] = `Experience #${index + 1}: Start date must be in YYYY-MM format`;
    }
    if (exp.end_date && !/^\d{4}-\d{2}$/.test(exp.end_date)) {
      errors[`experience_${index}_end_date`] = `Experience #${index + 1}: End date must be in YYYY-MM format`;
    }
  });
  return errors;
};

// Validate education entries
const validateEducation = (educationList) => {
  const errors = {};
  educationList.forEach((edu, index) => {
    if (!edu.degree?.trim()) {
      errors[`education_${index}_degree`] = `Education #${index + 1}: Degree is required`;
    }
    if (!edu.institution?.trim()) {
      errors[`education_${index}_institution`] = `Education #${index + 1}: Institution is required`;
    }
    
    // Validate date format (YYYY-MM)
    if (edu.start_date && !/^\d{4}-\d{2}$/.test(edu.start_date)) {
      errors[`education_${index}_start_date`] = `Education #${index + 1}: Start date must be in YYYY-MM format`;
    }
    if (edu.end_date && !/^\d{4}-\d{2}$/.test(edu.end_date)) {
      errors[`education_${index}_end_date`] = `Education #${index + 1}: End date must be in YYYY-MM format`;
    }
  });
  return errors;
};

// Validate project entries
const validateProjects = (projectsList) => {
  const errors = {};
  projectsList.forEach((project, index) => {
    if (!project.name?.trim()) {
      errors[`project_${index}_name`] = `Project #${index + 1}: Project name is required`;
    }
    if (!project.description?.trim()) {
      errors[`project_${index}_description`] = `Project #${index + 1}: Description is required`;
    }
    
    // Validate date format (YYYY-MM) if provided
    if (project.start_date && !/^\d{4}-\d{2}$/.test(project.start_date)) {
      errors[`project_${index}_start_date`] = `Project #${index + 1}: Start date must be in YYYY-MM format`;
    }
    if (project.end_date && !/^\d{4}-\d{2}$/.test(project.end_date)) {
      errors[`project_${index}_end_date`] = `Project #${index + 1}: End date must be in YYYY-MM format`;
    }
  });
  return errors;
};

// Utility function to parse gaps from different formats
const parseGapsData = (gapsData) => {
  let gaps = [];
  
  if (!gapsData) return gaps;
  
  // If it's already an array, return it
  if (Array.isArray(gapsData)) {
    return gapsData;
  }
  
  // If it's an object, extract the values
  if (typeof gapsData === 'object' && gapsData !== null) {
    return Object.values(gapsData);
  }
  
  // If it's a string, try to parse it
  if (typeof gapsData === 'string') {
    try {
      // First, try to parse as JSON (handle both array and object formats)
      let parsed;
      
      // Handle single quotes by replacing with double quotes
      const normalizedString = gapsData.replace(/'/g, '"');
      parsed = JSON.parse(normalizedString);
      
      // If parsed successfully, handle both array and object cases
      if (Array.isArray(parsed)) {
        return parsed;
      } else if (typeof parsed === 'object' && parsed !== null) {
        return Object.values(parsed);
      }
    } catch (parseError) {
      // If JSON parsing fails, try manual parsing for array format
      if (gapsData.trim().startsWith('[') && gapsData.trim().endsWith(']')) {
        try {
          // Remove brackets and split by commas, handling quotes
          const cleaned = gapsData.trim().slice(1, -1);
          gaps = cleaned
            .split(/,(?=(?:[^']*'[^']*')*[^']*$)/) // Split by commas not inside quotes
            .map(gap => gap.trim().replace(/^['"]|['"]$/g, '')) // Remove surrounding quotes
            .filter(gap => gap.length > 0);
          return gaps;
        } catch (error) {
          console.warn('Failed to manually parse array string:', error);
        }
      }
      
      // If all parsing fails, split by common delimiters
      gaps = gapsData
        .split(/[•\n;,]/)
        .map(gap => gap.trim())
        .filter(gap => gap.length > 0);
      
      // If no delimiters found, use the whole string as one item
      if (gaps.length === 0) {
        gaps = [gapsData.trim()];
      }
    }
  }
  
  return gaps;
};

const PersonalInfoForm = ({ nextPage = "/" }) => {
  const { user, setUser } = useContext(AuthContext);
  const { setError } = useContext(ErrorContext);
  const [searchParams] = useSearchParams();

  const authAxios = useMemo(() => {
    return createAxiosInstance(null);
  }, []);

  const skillsAPI = {
    getAll: () => authAxios.get("/skills/"),
    create: (name) => authAxios.post("/skills/", { name }),
    delete: (id) => authAxios.delete(`/skills/${id}/`),
  };

  const experienceAPI = {
    getAll: () => authAxios.get("/experiences/"),
    create: (data) => authAxios.post("/experiences/", data),
    update: (id, data) => authAxios.patch(`/experiences/${id}/`, data),
    delete: (id) => authAxios.delete(`/experiences/${id}/`),
  };

  const educationAPI = {
    getAll: () => authAxios.get("/education/"),
    create: (data) => authAxios.post("/education/", data),
    update: (id, data) => authAxios.patch(`/education/${id}/`, data),
    delete: (id) => authAxios.delete(`/education/${id}/`),
  };

  const projectsAPI = {
    getAll: () => authAxios.get("/projects/"),
    create: (data) => authAxios.post("/projects/", data),
    update: (id, data) => authAxios.patch(`/projects/${id}/`, data),
    delete: (id) => authAxios.delete(`/projects/${id}/`),
  };

  // Grade color function
  // eslint-disable-next-line no-unused-vars
  const getGradeColor = (grade) => {
    switch (grade?.toUpperCase()) {
      case 'A':
        return '#4CAF50';
      case 'B':
        return '#8BC34A';
      case 'C':
        return '#FF9800';
      case 'D':
        return '#FF5722';
      case 'F':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  };

  const [formData, setFormData] = useState({
    email_address: "",
    first_name: "",
    last_name: "",
    date_of_birth: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    country: "",
    mobile_number: "",
  });

  // New sections state
  const [skills, setSkills] = useState([]);
  const [initialSkills, setInitialSkills] = useState([]); // To track skills initially loaded
  const [newSkill, setNewSkill] = useState("");
  const [experience, setExperience] = useState([
    {
      title: "",
      company: "",
      location: "",
      start_date: "",
      end_date: "",
      is_current: false,
      description: "",
      achievements: "",
    },
  ]);
  const [education, setEducation] = useState([]);
  const [projects, setProjects] = useState([]); // New state for projects
  const [initialProjects, setInitialProjects] = useState([]); // To track projects initially loaded
  const [initialData, setInitialData] = useState(formData);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Profile picture states
  const [profilePicture, setProfilePicture] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const resumeFileInputRef = useRef(null);

  // API loading states
  // eslint-disable-next-line no-unused-vars
  const [experienceLoading, setExperienceLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [educationLoading, setEducationLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [projectLoading, setProjectLoading] = useState(false); // New state for project loading
  // eslint-disable-next-line no-unused-vars
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [initialDataLoading, setInitialDataLoading] = useState(true); // Overall loading state

  // Resume autofill states
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeModalStep, setResumeModalStep] = useState("initial"); // 'initial', 'existing', 'categories', 'upload', 'parsing', 'analyzing', 'parsed'
  const [existingResumes, setExistingResumes] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [uploadedResumeId, setUploadedResumeId] = useState(null);
  const [resumeStatus, setResumeStatus] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);

  // Resume ranking states
  const [resumeRankingData, setResumeRankingData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [rankingLoading, setRankingLoading] = useState(false);
  

  
  // Mandatory field highlighting state
  const [highlightEmptyFields, setHighlightEmptyFields] = useState(false);
  
  // Track if initial load is complete to prevent reloading on profile picture change
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Helper function to get field styling with highlighting
  const getFieldStyle = (fieldName, fieldValue) => {
    // Basic personal information mandatory fields
    const basicMandatoryFields = [
      'first_name', 'last_name', 'date_of_birth', 
      'address_line_1', 'city', 'state', 'country', 'mobile_number'
    ];
    
    // Check if field is mandatory (basic fields or experience/education/project required fields)
    const isBasicMandatory = basicMandatoryFields.includes(fieldName);
    const isExperienceMandatory = fieldName.includes('_title') || fieldName.includes('_company') || fieldName.includes('_start_date') || (fieldName.includes('experience_') && fieldName.includes('_description'));
    const isEducationMandatory = fieldName.includes('_degree') || fieldName.includes('_institution') || fieldName.includes('_start_date');
    const isProjectMandatory = fieldName.includes('_name') || fieldName.includes('_start_date') || (fieldName.includes('project_') && fieldName.includes('_description'));
    
    const isMandatory = isBasicMandatory || isExperienceMandatory || isEducationMandatory || isProjectMandatory;
    
    const isEmpty = !fieldValue || fieldValue.trim() === '';
    const shouldHighlight = highlightEmptyFields && isMandatory && isEmpty;
    
    return shouldHighlight ? {
      backgroundColor: '#ffebee',
      border: '2px solid #ef9a9a',
      borderRadius: '8px',
      '&:hover': {
        backgroundColor: '#ffebee',
        borderRadius: '8px'
      },
      '&:focus': {
        backgroundColor: '#ffebee',
        border: '2px solid #ef9a9a',
        borderRadius: '8px'
      }
    } : {};
  };

  // Function to scroll to first empty mandatory field
  const scrollToFirstEmptyMandatoryField = () => {
    // First check if profile picture is missing
    if (!profilePicture) {
      const profileSection = document.querySelector('[data-section="profile-picture"]');
      if (profileSection) {
        profileSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }
    }
    
    // Check basic personal information fields
    const mandatoryFields = [
      { name: 'first_name', value: formData.first_name },
      { name: 'last_name', value: formData.last_name },
      { name: 'date_of_birth', value: formData.date_of_birth },
      { name: 'address_line_1', value: formData.address_line_1 },
      { name: 'city', value: formData.city },
      { name: 'state', value: formData.state },
      { name: 'country', value: formData.country },
      { name: 'mobile_number', value: formData.mobile_number }
    ];
    
    const firstEmptyBasicField = mandatoryFields.find(field => !field.value || field.value.trim() === '');
    
    if (firstEmptyBasicField) {
      const element = document.querySelector(`[name="${firstEmptyBasicField.name}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
        return true;
      }
    }
    
    // Check experience fields
    for (let i = 0; i < experience.length; i++) {
      const exp = experience[i];
      if (!exp.title?.trim()) {
        const element = document.querySelector(`[name="experience_${i}_title"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
      if (!exp.company?.trim()) {
        const element = document.querySelector(`[name="experience_${i}_company"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
      if (!exp.start_date?.trim()) {
        const element = document.querySelector(`[name="experience_${i}_start_date"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
      if (!exp.description?.trim()) {
        const element = document.querySelector(`[name="experience_${i}_description"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
    }
    
    // Check education fields
    for (let i = 0; i < education.length; i++) {
      const edu = education[i];
      if (!edu.degree?.trim()) {
        const element = document.querySelector(`[name="education_${i}_degree"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
      if (!edu.institution?.trim()) {
        const element = document.querySelector(`[name="education_${i}_institution"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
      if (!edu.start_date?.trim()) {
        const element = document.querySelector(`[name="education_${i}_start_date"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
    }
    
    // Check project fields
    for (let i = 0; i < projects.length; i++) {
      const project = projects[i];
      if (!project.name?.trim()) {
        const element = document.querySelector(`[name="project_${i}_name"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
      if (!project.start_date?.trim()) {
        const element = document.querySelector(`[name="project_${i}_start_date"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
      if (!project.description?.trim()) {
        const element = document.querySelector(`[name="project_${i}_description"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return true;
        }
      }
    }
    
    return false; // No empty fields found
  };

  // Load skills from API
  const loadSkills = async () => {
    setSkillsLoading(true);
    try {
      const response = await skillsAPI.getAll();
      const skillsData =
        response.data.results.length > 0
          ? response.data.results.map((skill) => ({
              id: skill.id,
              name: skill.name,
              level: skill.level || "",
              category: skill.category || "",
            }))
          : [];
      setSkills(skillsData);
      setInitialSkills(skillsData); // Store initial skills
    } catch (error) {
      console.error("Failed to load skills:", error);
      setError(`Failed to load skills: ${error.message || error}`);
      setSkills([]);
    } finally {
      setSkillsLoading(false);
    }
  };

  // Load experience from API
  const loadExperience = async () => {
    setExperienceLoading(true);
    try {
      const response = await experienceAPI.getAll();
      const experienceData =
        response.data.results.length > 0
          ? response.data.results.map((exp) => ({
              id: exp.id,
              title: exp.title || "",
              company: exp.company || "",
              location: exp.location || "",
              start_date: exp.start_date ? exp.start_date.substring(0, 7) : "",
              end_date: exp.end_date ? exp.end_date.substring(0, 7) : "",
              is_current: exp.is_current || false,
              description: exp.description || "",
              achievements: exp.achievements || "",
            }))
          : [];
      setExperience(experienceData);
    } catch (error) {
      console.error("Failed to load experience:", error);
      setExperience([
        {
          id: Date.now(),
          title: "",
          company: "",
          start_date: "",
          end_date: "",
          is_current: false,
          description: "",
        },
      ]);
    } finally {
      setExperienceLoading(false);
    }
  };

  // Load education from API
  const loadEducation = async () => {
    setEducationLoading(true);
    try {
      const response = await educationAPI.getAll();
      const educationData =
        response.data.results.length > 0
          ? response.data.results.map((edu) => ({
              id: edu.id,
              degree: edu.degree || "",
              institution: edu.institution || "",
              field_of_study: edu.field_of_study || "",
              location: edu.location || "",
              start_date: edu.start_date ? edu.start_date.substring(0, 7) : "",
              end_date: edu.end_date ? edu.end_date.substring(0, 7) : "",
              is_current: edu.is_current || false,
              gpa: edu.gpa || "",
              description: edu.description || "",
            }))
          : [];
      setEducation(educationData);
    } catch (error) {
      console.error("Failed to load education:", error);
      setEducation([
        {
          id: Date.now(),
          degree: "",
          institution: "",
          graduation_year: "",
        },
      ]);
    } finally {
      setEducationLoading(false);
    }
  };

  // Load projects from API
  const loadProjects = async () => {
    setProjectLoading(true);
    try {
      const response = await projectsAPI.getAll();
      const projectsData =
        response.data.results.length > 0
          ? response.data.results.map((project) => ({
              id: project.id,
              name: project.name || "",
              description: project.description || "",
              technologies: project.technologies || "",
              start_date: project.start_date
                ? project.start_date.substring(0, 7)
                : "",
              end_date: project.end_date
                ? project.end_date.substring(0, 7)
                : "",
              is_current: project.is_current || false,
              url: project.url || "",
              github_url: project.github_url || "",
              role: project.role || "",
              team_size: project.team_size || 0,
            }))
          : [];
      setProjects(projectsData);
      setInitialProjects(projectsData);
    } catch (error) {
      console.error("Failed to load projects:", error);
      setError(`Failed to load projects: ${error.message || error}`);
      setProjects([]);
    } finally {
      setProjectLoading(false);
    }
  };



  useEffect(() => {
    // Only reload form data and API data if this is the initial load
    // Don't reload when only profile_picture_url changes
    if (!initialLoadComplete && user?.id) {
      const newData = {
        email_address: user?.email_address ?? "",
        first_name: user?.first_name ?? "",
        middle_name: user?.middle_name ?? "",
        last_name: user?.last_name ?? "",
        date_of_birth: user?.date_of_birth ?? "",
        address_line_1: user?.address_line_1 ?? "",
        address_line_2: user?.address_line_2 ?? "",
        city: user?.city ?? "",
        state: user?.state ?? "",
        country: user?.country ?? "",
        mobile_number: user?.mobile_number ?? "",
      };
      setFormData(newData);
      setInitialData(newData);

      // Load data from APIs when user is available
      const loadAllData = async () => {
        try {
          await Promise.all([
            loadSkills(),
            loadExperience(),
            loadEducation(),
            loadProjects()
          ]);
        } catch (error) {
          console.error("Failed to load initial data:", error);
        } finally {
          setInitialDataLoading(false);
          setInitialLoadComplete(true);
        }
      };
      loadAllData();
    } else if (user === null && !initialLoadComplete) {
      // User is not logged in, stop loading
      setInitialDataLoading(false);
      setInitialLoadComplete(true);
    }
    
    // Always update profile picture when it changes (doesn't reload other data)
    if (user?.profile_picture_url) {
      setProfilePicture(user.profile_picture_url);
    } else if (user) {
      setProfilePicture(null);
    }
    
    // Keep loading if user is undefined (still being loaded by AuthContext)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, initialLoadComplete]);

  // Profile picture handling functions
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setError("Please select a valid image file (JPG, PNG, or WebP)");
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        return;
      }

      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setShowPreview(true);
    }
  };

  const handleUploadProfilePicture = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("profile_picture", selectedFile);

      const response = await authAxios.post("/profile-picture/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setError("Profile picture uploaded successfully!");
      setShowPreview(false);
      setSelectedFile(null);

      // Clean up preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }

      // Update user data with new profile picture URL
      if (response.data.profile_picture_url) {
        const updatedUser = {
          ...user,
          profile_picture_url: response.data.profile_picture_url,
        };
        setUser(updatedUser);
        // Use the direct URL for profile picture
        setProfilePicture(response.data.profile_picture_url);
      }
    } catch (error) {
      console.error("Upload error:", error);
      
      // Check for 413 status code (file too large)
      if (error.response?.status === 413) {
        setError("File is too large. Please upload a smaller image (recommended: under 5MB).");
      } else {
        setError(
          error.response?.data?.message || "Failed to upload profile picture"
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    try {
      await authAxios.delete("/profile-picture/");

      setProfilePicture(null);
      setError("Profile picture removed successfully!");
    } catch (error) {
      console.error("Delete error:", error);
      setError(
        error.response?.data?.message || "Failed to remove profile picture"
      );
    }
  };

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return "";
    const first = firstName ? firstName[0] : "";
    const last = lastName ? lastName[0] : "";
    return (first + last).toUpperCase();
  };

  // Convert technical status to user-friendly message
  const getUserFriendlyStatus = (status) => {
    if (!status) return null;

    switch (status.toUpperCase()) {
      case "ENRICHING":
        return "Analyzing your resume...";
      case "ENRICHED":
        return "Resume analysis complete!";
      case "NOT_ENRICHED":
        return "Preparing to analyze your resume...";
      default:
        return "Processing your resume...";
    }
  };

  // Note: fetchProtectedImage function removed - profile_picture_url is now a direct URL
  // that can be used directly as src without additional API calls

  // Skills handlers
  const handleAddSkill = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default form submission
      if (newSkill.trim()) {
        if (!skills.some((skill) => skill.name === newSkill.trim())) {
          try {
            const response = await skillsAPI.create(newSkill.trim());
            setSkills([
              ...skills,
              {
                id: response.data.id,
                name: response.data.name,
                level: response.data.level || "",
                category: response.data.category || "",
              },
            ]);
          } catch (error) {
            console.error("Failed to add skill:", error);
            setError(`Failed to add skill: ${error.message || error}`);
          }
        }
        setNewSkill("");
      }
    }
  };

  const handleRemoveSkill = async (skillToRemove) => {
    if (skillToRemove.id) {
      try {
        await skillsAPI.delete(skillToRemove.id);
        setSkills(skills.filter((skill) => skill.id !== skillToRemove.id));
      } catch (error) {
        console.error(
          `Failed to delete skill with ID ${skillToRemove.id}:`,
          error
        );
        setError(`Failed to delete skill: ${error.message || error}`);
      }
    } else {
      // If skill doesn't have an ID, it means it was never saved to backend, just remove from local state
      setSkills(skills.filter((skill) => skill.name !== skillToRemove.name));
    }
  };

  // Experience handlers
  const handleExperienceChange = (index, field, value) => {
    const updatedExperience = experience.map((exp, i) =>
      i === index ? { ...exp, [field]: value } : exp
    );
    setExperience(updatedExperience);
  };

  const addExperience = () => {
    setExperience([
      ...experience,
      {
        title: "",
        company: "",
        location: "",
        start_date: "",
        end_date: "",
        is_current: false,
        description: "",
        achievements: "",
      },
    ]);
  };

  const removeExperience = async (index) => {
    if (experience.length > 0) {
      const experienceToRemove = experience[index];
      if (experienceToRemove.id) {
        try {
          await experienceAPI.delete(experienceToRemove.id);
        } catch (error) {
          console.error(
            `Failed to delete experience with ID ${experienceToRemove.id}:`,
            error
          );
          // Optionally, re-throw or handle the error in a way that prevents local state update
          return; // Stop execution if API deletion fails
        }
      }
      setExperience(experience.filter((_, i) => i !== index));
    }
  };

  // Education handlers
  const handleEducationChange = (index, field, value) => {
    const updatedEducation = education.map((edu, i) =>
      i === index ? { ...edu, [field]: value } : edu
    );
    setEducation(updatedEducation);
  };

  const addEducation = () => {
    setEducation([
      ...education,
      {
        // No ID here, will be assigned by backend on creation
        degree: "",
        institution: "",
        field_of_study: "",
        location: "",
        start_date: "",
        end_date: "",
        is_current: false,
        gpa: "",
        description: "",
      },
    ]);
  };

  const removeEducation = async (index) => {
    if (education.length > 0) {
      const educationToRemove = education[index];
      if (educationToRemove.id) {
        try {
          await educationAPI.delete(educationToRemove.id);
        } catch (error) {
          console.error(
            `Failed to delete education with ID ${educationToRemove.id}:`,
            error
          );
          setError(`Failed to delete education: ${error.message || error}`);
          return;
        }
      }
      setEducation(education.filter((_, i) => i !== index));
    }
  };

  // Project handlers
  const handleProjectChange = (index, field, value) => {
    const updatedProjects = projects.map((project, i) =>
      i === index ? { ...project, [field]: value } : project
    );
    setProjects(updatedProjects);
  };

  const addProject = () => {
    setProjects([
      ...projects,
      {
        name: "",
        description: "",
        technologies: "",
        start_date: "",
        end_date: "",
        is_current: false,
        url: "",
        github_url: "",
        role: "",
        team_size: 0,
      },
    ]);
  };

  const removeProject = async (index) => {
    if (projects.length > 0) {
      const projectToRemove = projects[index];
      if (projectToRemove.id) {
        try {
          await projectsAPI.delete(projectToRemove.id);
        } catch (error) {
          console.error(
            `Failed to delete project with ID ${projectToRemove.id}:`,
            error
          );
          setError(`Failed to delete project: ${error.message || error}`);
          return;
        }
      }
      setProjects(projects.filter((_, i) => i !== index));
    }
  };

  // Resume API functions
  const fetchExistingResumes = async () => {
    setResumeLoading(true);
    try {
      const response = await authAxios.get("/user-resume/");
      setExistingResumes(response.data.results || response.data || []);
    } catch (error) {
      console.error("Error fetching resumes:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to fetch existing resumes";
      setError(errorMessage);
    } finally {
      setResumeLoading(false);
    }
  };

  const uploadResume = async (file) => {
    setResumeLoading(true);
    try {
      const formData = new FormData();
      formData.append("resume", file);

      console.log("Uploading file:", file.name, "Size:", file.size);

      const response = await authAxios.post("/user-resume/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      console.log("Upload response:", response.data);

      if (!response.data.id) {
        throw new Error("No resume ID returned from upload");
      }

      setUploadedResumeId(response.data.id);
      
      // Automatically trigger parsing after upload
      setResumeModalStep("parsing");
      try {
        await enrichResume(response.data.id);
        // Start status polling after successful enrich call
        startStatusPolling(response.data.id);
      } catch (parseError) {
        console.error("Auto-parse failed:", parseError);
        // If parse failed but it's already being processed, still start polling
        if (parseError.response?.data?.error?.includes("already being processed")) {
          startStatusPolling(response.data.id);
        } else {
          // Re-throw error if it's not about already processing
          throw parseError;
        }
      }
      
      return response.data.id;
    } catch (error) {
      console.error("Error uploading resume:", error);
      console.error("Error response:", error.response?.data);

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to upload resume";
      setError(errorMessage);
      throw error;
    } finally {
      setResumeLoading(false);
    }
  };

  const enrichResume = async (resumeId) => {
    try {
      console.log("Attempting to enrich resume with ID:", resumeId);
      const response = await authAxios.post(`/user-resume/${resumeId}/enrich/`);
      console.log("Enrich response:", response.data);
      
      return true;
    } catch (error) {
      console.error("Error enriching resume:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);

      // Handle specific case where resume is already being processed
      if (error.response?.data?.error === "Resume is already being processed") {
        console.log("Resume is already being processed, will continue checking status...");
        setError("Resume is already being processed. Checking status...");
        return true; // Don't throw error, just continue to status checking
      }

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        `Failed to start resume parsing (Status: ${error.response?.status})`;
      setError(errorMessage);
      throw error;
    }
  };

  // Add status polling functionality
  const startStatusPolling = (resumeId) => {
    let pollCount = 0;
    const maxPolls = 60; // Maximum 5 minutes of polling (60 * 5 seconds)
    
    const pollStatus = async () => {
      try {
        pollCount++;
        console.log(`Polling status attempt ${pollCount}/${maxPolls}`);
        
        const status = await checkResumeStatus(resumeId);
        
        if (status.status === "ENRICHED") {
          console.log("Resume processing completed (ENRICHED)!");
          
          // Stop polling and fetch parsed data with resume ranking
          try {
            await fetchParsedData(resumeId);
            setResumeModalStep("parsed");
          } catch (fetchError) {
            console.error("Failed to fetch parsed data:", fetchError);
            const errorMessage =
              fetchError.response?.data?.detail ||
              fetchError.response?.data?.message ||
              fetchError.response?.data?.error ||
              fetchError.message ||
              "Failed to fetch parsed resume data";
            setError(errorMessage);
            setResumeModalStep("initial");
          }
          
          return; // Stop polling
        } else if (status.status === "failed") {
          console.log("Resume processing failed!");
          setError("Resume processing failed. Please try again.");
          setResumeModalStep("initial");
          return; // Stop polling
        } else if (pollCount >= maxPolls) {
          console.log("Status polling timeout reached");
          setError("Resume processing is taking longer than expected. Please check back later.");
          setResumeModalStep("initial");
          return; // Stop polling
        } else {
          // Continue polling if status is "pending" or "processing"
          console.log(`Resume status: ${status.status}, continuing to poll...`);
          setTimeout(pollStatus, 5000); // Poll every 5 seconds
        }
      } catch (error) {
        console.error("Error during status polling:", error);
        pollCount++;
        
        if (pollCount >= maxPolls) {
          const errorMessage =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to check resume status. Please try again.";
          setError(errorMessage);
          setResumeModalStep("initial");
          return; // Stop polling
        }
        
        // Continue polling even if there's an error, but with a longer interval
        setTimeout(pollStatus, 10000); // Poll every 10 seconds on error
      }
    };
    
    // Start the first poll after a short delay
    setTimeout(pollStatus, 2000);
  };

  const checkResumeStatus = async (resumeId) => {
    try {
      const response = await authAxios.get(`/user-resume/${resumeId}/status/`);
      console.log("Resume status:", response.data);
      setResumeStatus(response.data);
      return response.data;
    } catch (error) {
      console.error("Error checking resume status:", error);
      console.error("Status error response:", error.response?.data);
      throw error;
    }
  };

  const fetchParsedData = async (resumeId) => {
    try {
      console.log("Fetching parsed data for resume ID:", resumeId);
      const response = await authAxios.get(
        `/user-resume/${resumeId}/parsed_data/`
      );
      console.log("Parsed data response:", response.data);
      setParsedData(response.data);

      // Start resume ranking analysis
      setRankingLoading(true);
      setResumeModalStep("analyzing");
      setError("Analyzing your resume - This may take a while");

      try {
        const rankingResult = await rankResumeWithErrorHandling(
          resumeId,
          null
        );

        if (rankingResult.success) {
          setResumeRankingData(rankingResult.data);
          setError("Resume analysis completed successfully!");
          // Categories are already cleared after successful category association above
        } else {
          console.error("Resume ranking failed:", rankingResult.error);
          setError("Failed to analyze resume ranking: " + rankingResult.error);
        }
      } catch (error) {
        console.error("Resume ranking error:", error);
        setError("Failed to analyze resume ranking");
      } finally {
        setRankingLoading(false);
        setResumeModalStep("parsed");
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching parsed data:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to fetch parsed resume data";
      setError(errorMessage);
      throw error;
    }
  };

  // ...existing code...

  // eslint-disable-next-line no-unused-vars
  const markResumePreferred = async (resumeId) => {
    try {
      await authAxios.patch(`/user-resume/${resumeId}/`, { preferred: true });
      return true;
    } catch (error) {
      console.error("Error marking resume as preferred:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to mark resume as preferred";
      setError(errorMessage);
      throw error;
    }
  };

  // Resume autofill handlers
  // eslint-disable-next-line no-unused-vars
  const handleResumeFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (file.type !== "application/pdf") {
        setError("Please select a PDF file");
        // Reset the file input safely
        if (event.target) {
          event.target.value = "";
        }
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        // Reset the file input safely
        if (event.target) {
          event.target.value = "";
        }
        return;
      }

      try {
        const resumeId = await uploadResume(file);
        setUploadedResumeId(resumeId);
        // Upload function now automatically triggers parsing, so no need to set step here
      } catch (error) {
        // Error handled in uploadResume
      }

      // Reset the file input safely
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Check if all mandatory fields are filled
  const areAllMandatoryFieldsFilled = () => {
    // Check basic personal information fields (all mandatory)
    const basicFieldsFilled = (
      formData.first_name?.trim() &&
      formData.last_name?.trim() &&
      formData.date_of_birth &&
      formData.address_line_1?.trim() &&
      formData.city?.trim() &&
      formData.state?.trim() &&
      formData.country?.trim() &&
      formData.mobile_number?.trim()
    );

    // Check if profile picture is uploaded (mandatory)
    const profilePictureUploaded = profilePicture !== null;

    // Check experience mandatory fields (if there are any experience entries)
    const experienceFieldsFilled = experience.length === 0 || experience.every(exp => 
      exp.title?.trim() && 
      exp.company?.trim() && 
      exp.start_date?.trim() &&
      exp.description?.trim()
    );

    // Check education mandatory fields (if there are any education entries)  
    const educationFieldsFilled = education.length === 0 || education.every(edu => 
      edu.degree?.trim() && 
      edu.institution?.trim() && 
      edu.start_date?.trim()
    );

    // Check project mandatory fields (if there are any project entries)
    const projectFieldsFilled = projects.length === 0 || projects.every(project => 
      project.name?.trim() &&
      project.start_date?.trim() &&
      project.description?.trim()
    );

    return basicFieldsFilled && profilePictureUploaded && experienceFieldsFilled && educationFieldsFilled && projectFieldsFilled;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // First, check if profile picture is uploaded (mandatory)
    if (!profilePicture) {
      setError("Please upload a profile picture to continue");
      // Scroll to profile picture section
      const profileSection = document.querySelector('[data-section="profile-picture"]') || 
                           document.querySelector('.profile-picture-section') ||
                           document.querySelector('input[type="file"]');
      if (profileSection) {
        profileSection.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    
    // Check if all mandatory fields are filled
    if (!areAllMandatoryFieldsFilled()) {
      // Enable highlighting and scroll to first empty field
      setHighlightEmptyFields(true);
      const foundEmptyField = scrollToFirstEmptyMandatoryField();
      if (foundEmptyField) {
        setError("Please fill in all required fields");
        return;
      }
    }
    
    // Validate all forms
    const basicValidationErrors = validate(formData);
    const experienceValidationErrors = validateExperience(experience);
    const educationValidationErrors = validateEducation(education);
    const projectValidationErrors = validateProjects(projects);
    
    // Combine all validation errors
    const allValidationErrors = {
      ...basicValidationErrors,
      ...experienceValidationErrors,
      ...educationValidationErrors,
      ...projectValidationErrors
    };
    
    setErrors(allValidationErrors);
    
    if (Object.keys(allValidationErrors).length > 0) {
      // Enable highlighting when there are validation errors
      setHighlightEmptyFields(true);
      // Find the first error field
      const firstErrorKey = Object.keys(allValidationErrors)[0];
      let firstErrorField = document.querySelector(`[name="${firstErrorKey}"]`);
      
      // If it's a complex field (experience/education/project), try to find it by a more generic approach
      if (!firstErrorField) {
        // For experience/education/project errors, scroll to the relevant section
        if (firstErrorKey.includes('experience')) {
          firstErrorField = document.querySelector('[data-section="experience"]');
        } else if (firstErrorKey.includes('education')) {
          firstErrorField = document.querySelector('[data-section="education"]');
        } else if (firstErrorKey.includes('project')) {
          firstErrorField = document.querySelector('[data-section="projects"]');
        }
      }
      
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        if (firstErrorField.focus) {
          firstErrorField.focus();
        }
      }
      
      // Show the first error message
      setError(allValidationErrors[firstErrorKey]);
      return;
    }
    
    try {
      // Save basic profile info to backend
      const backendPayload = { ...formData };

      console.log("Submitting profile to backend:", backendPayload);
      let hasErrors = false;

      try {
        const response = await authAxios.patch("/self/", backendPayload);
        setUser(response.data);
        setInitialData(formData);
      } catch (error) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          "Failed to update profile";
        setError(errorMessage);
        hasErrors = true;
      }

      // Skills persistence logic
      const skillsToCreate = skills.filter(
        (skill) =>
          !skill.id && !initialSkills.some((is) => is.name === skill.name)
      );
      const skillsToDelete = initialSkills.filter(
        (initialSkill) =>
          !skills.some((currentSkill) => currentSkill.id === initialSkill.id)
      );

      const skillPromises = [];

      // Create new skills
      for (const skill of skillsToCreate) {
        skillPromises.push(
          // eslint-disable-next-line no-loop-func
          (async () => {
            try {
              const response = await skillsAPI.create(skill.name);
              return { ...skill, id: response.data.id };
            } catch (error) {
              console.error(`Failed to create skill ${skill.name}:`, error);
              setError(`Failed to create skill: ${error.message || error}`);
              hasErrors = true;
              return skill; // Return original skill to maintain local state
            }
          })()
        );
      }

      // Delete removed skills
      for (const skill of skillsToDelete) {
        skillPromises.push(
          // eslint-disable-next-line no-loop-func
          (async () => {
            if (skill.id) {
              // Only delete if it has a backend ID
              try {
                await skillsAPI.delete(skill.id);
              } catch (error) {
                console.error(
                  `Failed to delete skill with ID ${skill.id}:`,
                  error
                );
                setError(`Failed to delete skill: ${error.message || error}`);
                hasErrors = true;
              }
            }
            return null; // Indicate deletion
          })()
        );
      }

      const results = await Promise.all(skillPromises);
      const updatedSkills = results.filter((result) => result !== null);
      setSkills(updatedSkills.filter((skill) => skill.id)); // Keep only skills with IDs
      setInitialSkills(updatedSkills.filter((skill) => skill.id)); // Update initial skills with persisted data

      // Save experience and education to APIs
      const experiencePromises = experience.map(async (exp) => {
        const formattedExpData = {
          title: exp.title,
          company: exp.company,
          location: exp.location,
          start_date: exp.start_date ? exp.start_date + "-01" : null,
          end_date: Boolean(exp.is_current)
            ? null
            : exp.end_date
            ? exp.end_date + "-01"
            : null,
          is_current: Boolean(exp.is_current),
          description: exp.description,
          achievements: exp.achievements || "",
        };

        if (exp.id) {
          try {
            await experienceAPI.update(exp.id, formattedExpData);
          } catch (updateError) {
            console.error(
              `Failed to update experience with ID ${exp.id}:`,
              updateError
            );
            setError(
              `Failed to update experience: ${
                updateError.message || updateError
              }`
            );
            hasErrors = true;
          }
        } else {
          try {
            const response = await experienceAPI.create(formattedExpData);
            return { ...exp, id: response.data.id };
          } catch (createError) {
            console.error("Failed to create new experience:", createError);
            setError(
              `Failed to create experience: ${
                createError.message || createError
              }`
            );
            hasErrors = true;
            return exp;
          }
        }
        return exp;
      });

      const updatedExperiences = await Promise.all(experiencePromises);
      setExperience(updatedExperiences);

      // Save education to APIs (similar logic as experience)
      const educationPromises = education.map(async (edu) => {
        const formattedEduData = {
          degree: edu.degree,
          institution: edu.institution,
          field_of_study: edu.field_of_study,
          location: edu.location || "",
          start_date: edu.start_date ? edu.start_date + "-01" : "",
          end_date: Boolean(edu.is_current)
            ? null
            : edu.end_date
            ? edu.end_date + "-01"
            : null,
          is_current: Boolean(edu.is_current),
          gpa: edu.gpa || "",
          description: edu.description || "",
        };

        if (edu.id) {
          try {
            await educationAPI.update(edu.id, formattedEduData);
          } catch (updateError) {
            console.error(
              `Failed to update education with ID ${edu.id}:`,
              updateError
            );
            setError(
              `Failed to update education: ${
                updateError.message || updateError
              }`
            );
            hasErrors = true;
          }
        } else {
          try {
            const response = await educationAPI.create(formattedEduData);
            return { ...edu, id: response.data.id };
          } catch (createError) {
            console.error("Failed to create new education:", createError);
            setError(
              `Failed to create education: ${
                createError.message || createError
              }`
            );
            hasErrors = true;
            return edu;
          }
        }
        return edu;
      });

      const updatedEducation = await Promise.all(educationPromises);
      setEducation(updatedEducation);

      // Projects persistence logic
      const projectsToCreate = projects.filter((project) => !project.id);
      const projectsToDelete = initialProjects.filter(
        (initialProject) =>
          !projects.some(
            (currentProject) => currentProject.id === initialProject.id
          )
      );

      const projectPromises = [];

      // Create new projects
      for (const project of projectsToCreate) {
        projectPromises.push(
          // eslint-disable-next-line no-loop-func
          (async () => {
            const formattedProjectData = {
              name: project.name,
              description: project.description || "",
              technologies: project.technologies || "",
              start_date: project.start_date
                ? project.start_date + "-01"
                : null,
              end_date: Boolean(project.is_current)
                ? null
                : project.end_date
                ? project.end_date + "-01"
                : null,
              is_current: Boolean(project.is_current),
              url: project.url || "",
              github_url: project.github_url || "",
              role: project.role || "",
              team_size: project.team_size || 0,
            };
            try {
              const response = await projectsAPI.create(formattedProjectData);
              return { ...project, id: response.data.id };
            } catch (error) {
              console.error(`Failed to create project ${project.name}:`, error);
              setError(`Failed to create project: ${error.message || error}`);
              hasErrors = true;
              return project;
            }
          })()
        );
      }

      // Update existing projects (only if they have an ID and changed - for now, just recreate and delete if there is no patch needed)
      // Since backend has patch endpoint, we use that.
      const projectsToUpdate = projects.filter(
        (project) =>
          project.id &&
          initialProjects.some(
            (initialProject) => initialProject.id === project.id
          )
      );
      for (const project of projectsToUpdate) {
        projectPromises.push(
          // eslint-disable-next-line no-loop-func
          (async () => {
            const formattedProjectData = {
              name: project.name,
              description: project.description || "",
              technologies: project.technologies || "",
              start_date: project.start_date
                ? project.start_date + "-01"
                : null,
              end_date: Boolean(project.is_current)
                ? null
                : project.end_date
                ? project.end_date + "-01"
                : null,
              is_current: Boolean(project.is_current),
              url: project.url || "",
              github_url: project.github_url || "",
              role: project.role || "",
              team_size: project.team_size || 0,
            };
            try {
              await projectsAPI.update(project.id, formattedProjectData);
              return project;
            } catch (error) {
              console.error(
                `Failed to update project ${project.name} with ID ${project.id}:`,
                error
              );
              setError(`Failed to update project: ${error.message || error}`);
              hasErrors = true;
              return project;
            }
          })()
        );
      }

      // Delete removed projects
      for (const project of projectsToDelete) {
        projectPromises.push(
          // eslint-disable-next-line no-loop-func
          (async () => {
            if (project.id) {
              try{
                await projectsAPI.delete(project.id);
              } catch (error) {
                console.error(
                  `Failed to delete project with ID ${project.id}:`,
                  error
                );
                setError(`Failed to delete project: ${error.message || error}`);
                hasErrors = true;
              }
            }
            return null;
          })()
        );
      }

      const projectResults = await Promise.all(projectPromises);
      const updatedProjects = projectResults.filter(
        (result) => result !== null
      );
      setProjects(updatedProjects.filter((project) => project.id));
      setInitialProjects(updatedProjects.filter((project) => project.id));

      if (!hasErrors && user) {
        setError(null);
        setSuccess(true);
        setHighlightEmptyFields(false); // Clear highlighting on success
        
        // Check for redirect parameter, otherwise go to profile
        const redirectUrl = searchParams.get('redirect');
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          window.location.href = '/profile';
        }
      } else if (hasErrors) {
        // If there were errors, don't redirect, just show toasts
        setSuccess(false);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred during profile save.";
      setError(errorMessage);
      setSuccess(false);
    }
  };

  const handleCancel = () => {
    setFormData(initialData);
    setErrors({});
  };

  return (
    <>
      {initialDataLoading ? (
        <Box
          sx={{
            minHeight: "100vh",
            backgroundColor: "white",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          <CircularProgress size={60} sx={{ color: 'primary.main', mb: 3 }} />
          <Typography variant="h6" sx={{ color: 'black', mb: 1 }}>
            Loading your profile...
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Please wait while we fetch your information
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            minHeight: "100vh",
            backgroundColor: "white",
            py: 6,
            px: 2,
          }}
        >
          <Card
            sx={{ maxWidth: 1200, mx: "auto", borderRadius: 0, boxShadow: 'none', border: '1px solid #e0e0e0', p: 3 }}
          >
        <CardContent>
          {/* Profile Picture Section */}
          <Box
            data-section="profile-picture"
            sx={{
              textAlign: "center",
              mb: 4,
              p: 3,
              bgcolor: "grey.50",
              borderRadius: 0,
              border: highlightEmptyFields && !profilePicture ? '2px solid #f44336' : '1px solid #e0e0e0',
            }}
          >
            <Typography
              variant="h6"
              fontWeight={600}
              color="primary.main"
              sx={{ mb: 2 }}
            >
              Profile Picture
            </Typography>
            <Box sx={{ position: "relative", display: "inline-block", mb: 2 }}>
              <Avatar
                src={profilePicture}
                sx={{
                  width: 120,
                  height: 120,
                  fontSize: 48,
                  bgcolor: "primary.main",
                  border: "4px solid white",
                  boxShadow: 'none',
                }}
              >
                {!profilePicture &&
                  !imageLoading &&
                  getInitials(formData.first_name, formData.last_name)}
                {imageLoading && "..."}
              </Avatar>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  bgcolor: "primary.main",
                  color: "white",
                  "&:hover": { bgcolor: "primary.dark" },
                  boxShadow: 'none',
                }}
                size="small"
              >
                <PhotoCameraIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
              {profilePicture && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteProfilePicture}
                  size="small"
                >
                  Remove
                </Button>
              )}
            </Box>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              style={{ display: "none" }}
            />
          </Box>

          {/* Autofill with Resume Button */}
          <Box
            sx={{
              textAlign: "center",
              mt: 3,
              mb: 2,
              display: "flex",
              gap: 2,
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              color="info"
              size="large"
              startIcon={<UploadFileIcon sx={{ fontSize: 28 }} />}
              onClick={() => {
                setShowResumeModal(true);
                setResumeModalStep("initial");
              }}
              sx={{ 
                fontWeight: 600,
                fontSize: '1.1rem',
                py: 1.5,
                px: 4
              }}
            >
              Upload Your Resume & Autofill Profile
            </Button>
          </Box>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Personal Information Section */}
            <Typography
              variant="h6"
              fontWeight={600}
              color="primary.main"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <PersonIcon sx={{ fontSize: 24 }} /> Personal Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {/* Row 1 */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  name="first_name"
                  label="First Name"
                  value={formData.first_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.first_name}
                  helperText={errors.first_name}
                  autoComplete="given-name"
                  sx={getFieldStyle('first_name', formData.first_name)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  name="middle_name"
                  label="Middle Name (Optional)"
                  value={formData.middle_name}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="additional-name"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  name="last_name"
                  label="Last Name"
                  value={formData.last_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.last_name}
                  helperText={errors.last_name}
                  autoComplete="family-name"
                  sx={getFieldStyle('last_name', formData.last_name)}
                />
              </Grid>
              {/* Row 2 */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  name="email_address"
                  label="Email Address"
                  value={formData.email_address}
                  fullWidth
                  required
                  disabled
                  autoComplete="email"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  name="date_of_birth"
                  label="Date of Birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.date_of_birth}
                  helperText={errors.date_of_birth}
                  autoComplete="bday"
                  sx={getFieldStyle('date_of_birth', formData.date_of_birth)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  name="mobile_number"
                  label="Mobile Number"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.mobile_number}
                  helperText={errors.mobile_number}
                  autoComplete="tel"
                  sx={{ 
                    ml: { sm: 0, md: 1 },
                    ...getFieldStyle('mobile_number', formData.mobile_number)
                  }}
                />
              </Grid>
            </Grid>
            {/* Address Section */}
            <Typography
              variant="h6"
              fontWeight={600}
              color="primary.main"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <HomeIcon sx={{ fontSize: 24 }} /> Address
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="address_line_1"
                  label="Address Line 1"
                  value={formData.address_line_1}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.address_line_1}
                  helperText={errors.address_line_1}
                  autoComplete="address-line1"
                  sx={getFieldStyle('address_line_1', formData.address_line_1)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="address_line_2"
                  label="Address Line 2 (Optional)"
                  value={formData.address_line_2}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="address-line2"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="city"
                  label="City"
                  value={formData.city}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.city}
                  helperText={errors.city}
                  autoComplete="address-level2"
                  sx={getFieldStyle('city', formData.city)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="state"
                  label="State/Province"
                  value={formData.state}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!errors.state}
                  helperText={errors.state}
                  autoComplete="address-level1"
                  sx={getFieldStyle('state', formData.state)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="country"
                  label="Country"
                  value={formData.country}
                  onChange={handleChange}
                  fullWidth
                  required
                  select
                  error={!!errors.country}
                  helperText={errors.country}
                  autoComplete="country"
                  sx={{ 
                    minWidth: 200, 
                    width: "100%",
                    ...getFieldStyle('country', formData.country)
                  }}
                >
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Skills Section */}
            <Typography
              variant="h6"
              fontWeight={600}
              color="primary.main"
              sx={{
                mb: 2,
                mt: 4,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <SkillsIcon sx={{ fontSize: 24 }} /> Skills
            </Typography>
            <Box sx={{ mb: 4 }}>
              <TextField
                label="Add a skill (Press Enter to add)"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={handleAddSkill}
                fullWidth
                placeholder="e.g., JavaScript, Python, React..."
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {skills.map((skill, index) => (
                  <Chip
                    key={skill.id || `skill-${index}`}
                    label={skill.name}
                    onDelete={() => handleRemoveSkill(skill)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
                {skills.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No skills added yet. Type a skill above and press Enter to
                    add it.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Experience Section */}
            <Typography
              variant="h6"
              fontWeight={600}
              color="primary.main"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <WorkIcon sx={{ fontSize: 24 }} /> Work Experience
            </Typography>
            {experience.map((exp, index) => (
              <Card
                key={exp.id || `exp-${index}`}
                sx={{ mb: 2, border: "1px solid #e0e0e0" }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Experience #{index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => removeExperience(index)}
                      color="error"
                      size="small"
                    >
                      <Trash2 size={20} color="#d32f2f" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Job Title *"
                        name="job_title"
                        value={exp.title}
                        onChange={(e) =>
                          handleExperienceChange(index, "title", e.target.value)
                        }
                        fullWidth
                        required
                        error={!!errors[`experience_${index}_title`]}
                        helperText={errors[`experience_${index}_title`]}
                        sx={getFieldStyle(`experience_${index}_title`, exp.title)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Company *"
                        value={exp.company}
                        onChange={(e) =>
                          handleExperienceChange(
                            index,
                            "company",
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                        error={!!errors[`experience_${index}_company`]}
                        helperText={errors[`experience_${index}_company`]}
                        sx={getFieldStyle(`experience_${index}_company`, exp.company)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Start Date *"
                        type="month"
                        value={exp.start_date}
                        onChange={(e) =>
                          handleExperienceChange(
                            index,
                            "start_date",
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={getFieldStyle(`experience_${index}_start_date`, exp.start_date)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="End Date"
                        type="month"
                        value={exp.end_date}
                        onChange={(e) =>
                          handleExperienceChange(
                            index,
                            "end_date",
                            e.target.value
                          )
                        }
                        fullWidth
                        disabled={exp.is_current}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={exp.is_current}
                            onChange={(e) =>
                              handleExperienceChange(
                                index,
                                "is_current",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="I currently work here"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Job Description *"
                        name={`experience_${index}_description`}
                        value={exp.description}
                        onChange={(e) =>
                          handleExperienceChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        required
                        placeholder="Describe your responsibilities and achievements..."
                        error={!!errors[`experience_${index}_description`]}
                        helperText={errors[`experience_${index}_description`]}
                        sx={getFieldStyle(`experience_${index}_description`, exp.description)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={addExperience}
              variant="outlined"
              sx={{ mb: 4 }}
            >
              Add Experience
            </Button>

            {/* Education Section */}
            <Typography
              variant="h6"
              fontWeight={600}
              color="primary.main"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <SchoolIcon sx={{ fontSize: 24 }} /> Education
            </Typography>
            {education.map((edu, index) => (
              <Card
                key={edu.id || `edu-${index}`}
                sx={{ mb: 2, border: "1px solid #e0e0e0" }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Education #{index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => removeEducation(index)}
                      color="error"
                      size="small"
                    >
                      <Trash2 size={20} color="#d32f2f" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Degree"
                        value={edu.degree}
                        onChange={(e) =>
                          handleEducationChange(index, "degree", e.target.value)
                        }
                        fullWidth
                        required
                        placeholder="e.g., Bachelor of Science in Computer Science"
                        sx={getFieldStyle(`education_${index}_degree`, edu.degree)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Field of Study"
                        value={edu.field_of_study}
                        onChange={(e) =>
                          handleEducationChange(
                            index,
                            "field_of_study",
                            e.target.value
                          )
                        }
                        fullWidth
                        placeholder="e.g., Computer Science"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Institution"
                        value={edu.institution}
                        onChange={(e) =>
                          handleEducationChange(
                            index,
                            "institution",
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                        placeholder="e.g., University of Example"
                        sx={getFieldStyle(`education_${index}_institution`, edu.institution)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Location"
                        value={edu.location}
                        onChange={(e) =>
                          handleEducationChange(
                            index,
                            "location",
                            e.target.value
                          )
                        }
                        fullWidth
                        placeholder="e.g., New York, NY"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Start Date (YYYY-MM)"
                        type="month"
                        value={edu.start_date}
                        onChange={(e) =>
                          handleEducationChange(
                            index,
                            "start_date",
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={getFieldStyle(`education_${index}_start_date`, edu.start_date)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="End Date (YYYY-MM)"
                        type="month"
                        value={edu.end_date}
                        onChange={(e) =>
                          handleEducationChange(
                            index,
                            "end_date",
                            e.target.value
                          )
                        }
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        disabled={edu.is_current}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={edu.is_current}
                            onChange={(e) => {
                              handleEducationChange(
                                index,
                                "is_current",
                                e.target.checked
                              );
                              if (e.target.checked) {
                                handleEducationChange(index, "end_date", "");
                              }
                            }}
                          />
                        }
                        label="I currently study here"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="GPA (optional)"
                        value={edu.gpa}
                        onChange={(e) =>
                          handleEducationChange(index, "gpa", e.target.value)
                        }
                        fullWidth
                        placeholder="e.g., 3.85"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Description (optional)"
                        value={edu.description}
                        onChange={(e) =>
                          handleEducationChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="e.g., Focused on software engineering with emphasis on web development and algorithms"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={addEducation}
              variant="outlined"
              sx={{ mb: 4 }}
            >
              Add Education
            </Button>

            {/* Projects Section */}
            <Typography
              variant="h6"
              fontWeight={600}
              color="primary.main"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <WorkIcon sx={{ fontSize: 24 }} /> Projects
            </Typography>
            {projects.map((project, index) => (
              <Card
                key={project.id || `project-${index}`}
                sx={{ mb: 2, border: "1px solid #e0e0e0" }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Project #{index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => removeProject(index)}
                      color="error"
                      size="small"
                    >
                      <Trash2 size={20} color="#d32f2f" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Project Name *"
                        name="project_name"
                        value={project.name}
                        onChange={(e) =>
                          handleProjectChange(index, "name", e.target.value)
                        }
                        fullWidth
                        required
                        error={!!errors[`project_${index}_name`]}
                        helperText={errors[`project_${index}_name`]}
                        sx={getFieldStyle(`project_${index}_name`, project.name)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Technologies Used"
                        value={project.technologies}
                        onChange={(e) =>
                          handleProjectChange(
                            index,
                            "technologies",
                            e.target.value
                          )
                        }
                        fullWidth
                        placeholder="e.g., React, Node.js, MongoDB"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Start Date *"
                        type="month"
                        value={project.start_date}
                        onChange={(e) =>
                          handleProjectChange(
                            index,
                            "start_date",
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        error={!!errors[`project_${index}_start_date`]}
                        helperText={errors[`project_${index}_start_date`]}
                        sx={getFieldStyle(`project_${index}_start_date`, project.start_date)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="End Date"
                        type="month"
                        value={project.end_date}
                        onChange={(e) =>
                          handleProjectChange(index, "end_date", e.target.value)
                        }
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        disabled={project.is_current}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={project.is_current}
                            onChange={(e) =>
                              handleProjectChange(
                                index,
                                "is_current",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="I currently work on this project"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Project Description *"
                        name={`project_${index}_description`}
                        value={project.description}
                        onChange={(e) =>
                          handleProjectChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        required
                        placeholder="Describe the project, its purpose, and your role."
                        error={!!errors[`project_${index}_description`]}
                        helperText={errors[`project_${index}_description`]}
                        sx={getFieldStyle(`project_${index}_description`, project.description)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="URL (optional)"
                        value={project.url}
                        onChange={(e) =>
                          handleProjectChange(index, "url", e.target.value)
                        }
                        fullWidth
                        placeholder="e.g., https://example.com"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="GitHub URL (optional)"
                        value={project.github_url}
                        onChange={(e) =>
                          handleProjectChange(
                            index,
                            "github_url",
                            e.target.value
                          )
                        }
                        fullWidth
                        placeholder="e.g., https://github.com/user/repo"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Role"
                        value={project.role}
                        onChange={(e) =>
                          handleProjectChange(index, "role", e.target.value)
                        }
                        fullWidth
                        placeholder="e.g., Frontend Developer"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Team Size (optional)"
                        type="number"
                        value={project.team_size}
                        onChange={(e) =>
                          handleProjectChange(
                            index,
                            "team_size",
                            parseInt(e.target.value) || 0
                          )
                        }
                        fullWidth
                        placeholder="e.g., 5"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={addProject}
              variant="outlined"
              sx={{ mb: 4 }}
            >
              Add Project
            </Button>

            {/* Save/Cancel Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 4,
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                sx={{ fontWeight: 600, px: 4 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                sx={{ 
                  fontWeight: 600, 
                  px: 4
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Snackbar
        open={success}
        autoHideDuration={5000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        slots={{ transition: Slide }}
      >
        <Alert
          onClose={() => setSuccess(false)}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            fontSize: "0.95rem",
            fontWeight: 500,
            borderRadius: 0,
            boxShadow: 'none',
            "& .MuiAlert-icon": {
              fontSize: "1.2rem",
            },
          }}
        >
          Profile updated successfully!
        </Alert>
      </Snackbar>

      {/* Profile Picture Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Preview Profile Picture</DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          {previewUrl && (
            <Avatar
              src={previewUrl}
              sx={{
                width: 200,
                height: 200,
                mx: "auto",
                mb: 2,
                boxShadow: 'none',
              }}
            />
          )}
          <Typography variant="body2" color="text.secondary">
            This will be your new profile picture
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowPreview(false);
              setSelectedFile(null);
              if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadProfilePicture}
            variant="contained"
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Autofill Modal */}
      <Dialog
        open={showResumeModal}
        onClose={() => {
          // Only allow closing if not in processing states
          if (resumeModalStep !== "parsing" && resumeModalStep !== "analyzing" && resumeModalStep !== "upload") {
            setShowResumeModal(false);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Autofill Profile with Resume
          {resumeLoading && <LinearProgress sx={{ mt: 1 }} />}
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400 }}>
          {/* Initial State - Choose Option */}
          {resumeModalStep === "initial" && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" gutterBottom>
                Choose how to proceed
              </Typography>
              <Stack
                direction="row"
                spacing={3}
                justifyContent="center"
                sx={{ mt: 4 }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<DescriptionIcon />}
                  onClick={async () => {
                    setResumeModalStep("existing");
                    await fetchExistingResumes();
                  }}
                  sx={{
                    minHeight: 100,
                    minWidth: 200,
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="body1">Get Existing Resumes</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Use previously uploaded resumes
                  </Typography>
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => {
                    // Trigger file input using a more robust approach
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = ".pdf";
                    input.style.display = "none";

                    const handleFileSelect = async (event) => {
                      const file = event.target?.files?.[0];
                      if (file) {
                        // Validate file type
                        if (file.type !== "application/pdf") {
                          setError("Please select a PDF file");
                          return;
                        }

                        // Validate file size (10MB limit)
                        if (file.size > 10 * 1024 * 1024) {
                          setError("File size must be less than 10MB");
                          return;
                        }

                        try {
                          // Upload the file directly
                          setResumeModalStep("upload");
                          const resumeId = await uploadResume(file);
                          setUploadedResumeId(resumeId);
                        } catch (error) {
                          setResumeModalStep("initial");
                        }
                      }

                      // Clean up
                      document.body.removeChild(input);
                    };

                    input.addEventListener("change", handleFileSelect);
                    document.body.appendChild(input);
                    input.click();
                  }}
                  sx={{
                    minHeight: 100,
                    minWidth: 200,
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="body1">Upload Resume</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Upload a new resume file
                  </Typography>
                </Button>
              </Stack>
            </Box>
          )}

          {/* Existing Resumes List */}
          {resumeModalStep === "existing" && (
            <Box>
              <Typography variant="h6" gutterBottom>
                My Existing Resumes
              </Typography>
              {resumeLoading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Loading resumes...
                  </Typography>
                </Box>
              ) : existingResumes.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <DescriptionIcon
                    sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                  />
                  <Typography variant="body1" color="text.secondary">
                    No resumes found. Please upload a resume first.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {existingResumes.map((resume) => (
                    <ListItemButton
                      key={resume.id}
                      selected={selectedResumeId === resume.id}
                      onClick={() => setSelectedResumeId(resume.id)}
                    >
                      <DescriptionIcon sx={{ mr: 2, color: "primary.main" }} />
                      <ListItemText
                        primary={resume.resume_name || `Resume ${resume.id}`}
                        secondary={`Uploaded: ${new Date(
                          resume.uploaded_at
                        ).toLocaleDateString()}`}
                      />
                      {resume.parsed_data && (
                        <Chip label="Parsed" color="success" size="small" />
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Upload Success */}
          {resumeModalStep === "upload" && uploadedResumeId && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CloudUploadIcon
                sx={{ fontSize: 48, color: "success.main", mb: 2 }}
              />
              <Typography variant="h6" color="success.main" gutterBottom>
                Resume Uploaded Successfully!
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Ready to parse your resume and extract information.
              </Typography>
            </Box>
          )}

          {/* Parsing Status */}
          {resumeModalStep === "parsing" && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Parsing Resume...
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getUserFriendlyStatus(resumeStatus?.status) ||
                  "Processing your resume data"}
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                onClick={async () => {
                  const currentResumeId = selectedResumeId || uploadedResumeId;
                  if (currentResumeId) {
                    try {
                      const status = await checkResumeStatus(currentResumeId);
                      console.log("Manual refresh status:", status);
                      if (status.status === "ENRICHED") {
                        // Stop any ongoing polling and fetch parsed data directly
                        try {
                          await fetchParsedData(currentResumeId, []);
                          setResumeModalStep("parsed");
                        } catch (fetchError) {
                          console.error("Failed to fetch parsed data:", fetchError);
                          const errorMessage =
                            fetchError.response?.data?.detail ||
                            fetchError.response?.data?.message ||
                            fetchError.response?.data?.error ||
                            fetchError.message ||
                            "Failed to fetch parsed resume data";
                          setError(errorMessage);
                        }
                      } else if (status.status === "failed") {
                        setError("Resume processing failed. Please try again.");
                        setResumeModalStep("initial");
                      }
                      // If still processing, do nothing - let the automatic polling continue
                    } catch (error) {
                      console.error("Error in manual refresh:", error);
                      const errorMessage =
                        error.response?.data?.detail ||
                        error.response?.data?.message ||
                        error.response?.data?.error ||
                        "Failed to refresh status";
                      setError(errorMessage);
                    }
                  }
                }}
                sx={{ mt: 2 }}
              >
                Refresh Status
              </Button>
            </Box>
          )}

          {/* Resume Ranking Analysis State */}
          {resumeModalStep === "analyzing" && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Analyzing your resume
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This may take a while
              </Typography>
              <Typography variant="caption" color="text.secondary">
                We're calculating your resume Score and providing
                detailed insights...
              </Typography>
            </Box>
          )}

          {/* Parsed Data Display */}
          {resumeModalStep === "parsed" && parsedData && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Resume Parsed Successfully!
              </Typography>

              {/* Resume Score Section */}
              {resumeRankingData && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: "primary.50",
                    borderRadius: 0,
                    border: "1px solid",
                    borderColor: "primary.200",
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                    Resume Evaluation
                  </Typography>
                  
                  {/* Grade and Score Display using Universal Component */}
                  <Box sx={{ mb: 3 }}>
                    <GradeScoreDisplay 
                      grade={resumeRankingData.grade} 
                      score={resumeRankingData.raw_score}
                      size="medium"
                      showLabels={true}
                    />
                  </Box>

                  {/* Justification */}
                  {resumeRankingData.justification && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Assessment Summary
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {resumeRankingData.justification}
                      </Typography>
                    </Box>
                  )}

                  {/* Areas for Improvement */}
                  {resumeRankingData.gaps_compared_to_target_job && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Areas for Improvement
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {parseGapsData(resumeRankingData.gaps_compared_to_target_job).map((gap, index) => (
                          <Typography
                            component="li"
                            key={index}
                            variant="body2"
                            sx={{ color: 'text.secondary', mb: 0.5 }}
                          >
                            {gap}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={<Checkbox defaultChecked />}
                  label="Mark as preferred resume"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Extracted Information Preview:
              </Typography>

              {/* ...existing parsed data display code... */}
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  bgcolor: "grey.50",
                  p: 2,
                  borderRadius: 0,
                }}
              >
                {parsedData.parsed_data && (
                  <Stack spacing={2}>
                    {/* Name and Contact */}
                    <Box>
                      <Typography variant="subtitle2" color="primary.main">
                        Personal Information
                      </Typography>
                      <Typography variant="body2">
                        Name: {parsedData.parsed_data.name}
                      </Typography>
                      <Typography variant="body2">
                        Email: {parsedData.parsed_data.contact?.email}
                      </Typography>
                      <Typography variant="body2">
                        Phone: {parsedData.parsed_data.contact?.phone}
                      </Typography>
                    </Box>

                    {/* Skills */}
                    {parsedData.parsed_data.skills && (
                      <Box>
                        <Typography variant="subtitle2" color="primary.main">
                          Skills ({parsedData.parsed_data.skills.length})
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 1,
                          }}
                        >
                          {parsedData.parsed_data.skills
                            .slice(0, 10)
                            .map((skill, idx) => (
                              <Chip
                                key={idx}
                                label={skill}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          {parsedData.parsed_data.skills.length > 10 && (
                            <Chip
                              label={`+${
                                parsedData.parsed_data.skills.length - 10
                              } more`}
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Experience */}
                    {parsedData.parsed_data.experience && (
                      <Box>
                        <Typography variant="subtitle2" color="primary.main">
                          Work Experience (
                          {parsedData.parsed_data.experience.length})
                        </Typography>
                        {parsedData.parsed_data.experience
                          .slice(0, 3)
                          .map((exp, idx) => (
                            <Box key={idx} sx={{ ml: 1, mt: 1 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {exp.title} at {exp.company}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {exp.start_date} - {exp.end_date}
                              </Typography>
                            </Box>
                          ))}
                        {parsedData.parsed_data.experience.length > 3 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            +{parsedData.parsed_data.experience.length - 3} more
                            positions
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Education */}
                    {parsedData.parsed_data.education && (
                      <Box>
                        <Typography variant="subtitle2" color="primary.main">
                          Education ({parsedData.parsed_data.education.length})
                        </Typography>
                        {parsedData.parsed_data.education.map((edu, idx) => (
                          <Box key={idx} sx={{ ml: 1, mt: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {edu.degree}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {edu.institution} - {edu.end_date}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Projects */}
                    {parsedData.parsed_data.projects &&
                      parsedData.parsed_data.projects.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" color="primary.main">
                            Projects ({parsedData.parsed_data.projects.length})
                          </Typography>
                          {parsedData.parsed_data.projects
                            .slice(0, 5)
                            .map((project, idx) => (
                              <Box key={idx} sx={{ ml: 1, mt: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {project.name || project}
                                </Typography>
                                {project.technologies && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Technologies: {project.technologies}
                                  </Typography>
                                )}
                                {project.description && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mt: 0.5 }}
                                  >
                                    {project.description.length > 100
                                      ? `${project.description.substring(
                                          0,
                                          100
                                        )}...`
                                      : project.description}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          {parsedData.parsed_data.projects.length > 5 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              +{parsedData.parsed_data.projects.length - 5} more
                              projects
                            </Typography>
                          )}
                        </Box>
                      )}
                  </Stack>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={() => {
              // Only allow cancel if not in processing states
              if (resumeModalStep !== "parsing" && resumeModalStep !== "analyzing" && resumeModalStep !== "upload") {
                setShowResumeModal(false);
              } else {
                setError("Cannot cancel while processing. Please wait for completion.");
              }
            }}
            disabled={resumeModalStep === "parsing" || resumeModalStep === "analyzing" || resumeModalStep === "upload"}
          >
            Cancel
          </Button>

          {resumeModalStep === "existing" && selectedResumeId && (
            <Button
              variant="contained"
              onClick={async () => {
                setResumeLoading(true);
                setResumeModalStep("parsing");
                
                try {
                  // First, check the resume status
                  const statusResponse = await authAxios.get(`/user-resume/${selectedResumeId}/status/`);
                  const { status, has_parsed_data } = statusResponse.data;
                  
                  // If status is ENRICHED and has parsed data, fetch it directly
                  if (status === "ENRICHED" && has_parsed_data) {
                    console.log("Resume is already enriched, fetching parsed data...");
                    
                    const parsedDataResponse = await authAxios.get(`/user-resume/${selectedResumeId}/parsed_data/`);
                    
                    if (parsedDataResponse.data) {
                      // Set the parsed data and move to parsed step
                      setParsedData(parsedDataResponse.data);
                      setResumeModalStep("parsed");
                      setResumeLoading(false);
                    } else {
                      throw new Error("No parsed data available despite ENRICHED status");
                    }
                  } else {
                    // Status is not ENRICHED, need to go through enrichment pipeline
                    console.log(`Resume status: ${status}, starting enrichment pipeline...`);
                    
                    try {
                      await enrichResume(selectedResumeId);
                      // Start centralized status polling (existing resume, no new categories)
                      startStatusPolling(selectedResumeId, []);
                    } catch (enrichError) {
                      // If enrichment failed (not because it's already processing), go back
                      if (!enrichError.response?.data?.error?.includes("already being processed")) {
                        setResumeModalStep("existing");
                        setResumeLoading(false);
                        setError(enrichError.response?.data?.message || "Failed to start enrichment");
                        return;
                      } else {
                        // If already processing, still start polling
                        console.log("Resume is already being processed, starting polling...");
                        startStatusPolling(selectedResumeId, []);
                      }
                    }
                  }
                } catch (error) {
                  console.error("Error processing existing resume:", error);
                  setError(error.response?.data?.message || "Failed to load resume data");
                  setResumeModalStep("existing");
                  setResumeLoading(false);
                }
              }}
              disabled={resumeLoading}
            >
              {resumeLoading ? "Loading..." : "Use This Resume"}
            </Button>
          )}

          {/* No Parse Resume button for upload step since it's automatic */}

          {resumeModalStep === "parsed" && parsedData && (
            <Button
              variant="contained"
              onClick={async () => {
                // Populate form fields with parsed data
                const data = parsedData.parsed_data;

                // Parse name into first/last name
                const nameParts = data.name ? data.name.split(" ") : [];
                const firstName = nameParts[0] || "";
                const lastName = nameParts.slice(1).join(" ") || "";

                // Update form data with parsed information
                setFormData((prev) => ({
                  ...prev,
                  first_name: firstName,
                  last_name: lastName,
                  mobile_number: data.contact?.phone || prev.mobile_number,
                  // Keep existing address and other fields
                }));

                // Update skills
                if (data.skills && Array.isArray(data.skills)) {
                  const formattedSkills = data.skills.map((skillName) => ({
                    id: null, // No ID from parser, will be assigned on backend creation
                    name: skillName,
                    level: "",
                    category: "",
                  }));
                  setSkills(formattedSkills);
                }

                // Update experience
                if (data.experience && Array.isArray(data.experience)) {
                  const formattedExperience = data.experience.map(
                    (exp, index) => {
                      const isPresent =
                        exp.end_date &&
                        exp.end_date.toLowerCase() === "present";
                      return {
                        // id: Date.now() + index, // Removed frontend ID assignment
                        title: exp.title || "",
                        company: exp.company || "",
                        location: exp.location || "",
                        start_date: exp.start_date
                          ? exp.start_date.replace(/present/gi, "")
                          : "",
                        end_date: isPresent ? "" : exp.end_date || "",
                        is_current: isPresent,
                        description: exp.description || "",
                        achievements: exp.achievements || "",
                      };
                    }
                  );
                  setExperience(formattedExperience);
                }

                // Update education
                if (data.education && Array.isArray(data.education)) {
                  const formattedEducation = data.education.map(
                    (edu, index) => {
                      const isPresent =
                        edu.end_date &&
                        edu.end_date.toLowerCase() === "present";
                      return {
                        // id: Date.now() + index + 1000, // Removed frontend ID assignment
                        degree: edu.degree || "",
                        institution: edu.institution || "",
                        field_of_study: edu.field_of_study || "",
                        location: edu.location || "",
                        start_date: edu.start_date
                          ? edu.start_date.replace(/present/gi, "")
                          : "",
                        end_date: isPresent ? "" : edu.end_date || "",
                        is_current: isPresent,
                        gpa: edu.gpa || "",
                        description: edu.description || "",
                      };
                    }
                  );
                  setEducation(formattedEducation);
                }

                // Update projects
                if (data.projects && Array.isArray(data.projects)) {
                  const formattedProjects = data.projects.map(
                    (project, index) => ({
                      // id: Date.now() + index + 2000, // Removed frontend ID assignment
                      name: project.name || project || "",
                      description: project.description || "",
                      technologies: project.technologies || "",
                      start_date: "",
                      end_date: "",
                      is_current: false,
                      url: "",
                      github_url: "",
                      role: "",
                      team_size: 0,
                    })
                  );
                  setProjects(formattedProjects);
                }

                setSuccess(true);
                setShowResumeModal(false);
                
                // Highlight empty mandatory fields after autofill
                setHighlightEmptyFields(true);
              }}
              disabled={resumeLoading}
            >
              Apply to Profile
            </Button>
          )}
        </DialogActions>
      </Dialog>
        </Box>
      )}
    </>
  );
};

export default PersonalInfoForm;
